import React, { useMemo } from 'react';
import {
    Box,
    Chip,
    Drawer,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    DensityContext,
    StatusBadge,
} from '../../qCockpit/components/SharedFrames';
import StrategySnapshotPanel from '../../qCockpit/components/StrategySnapshotPanel';
import StrategyEventFeedPanel from '../../qCockpit/components/StrategyEventFeedPanel';
import { useStrategyInstanceView } from '../../qCockpit/hooks/useStrategyInstanceView';
import { clientColumnKey } from '../clientAlias';
import { shellTokens } from '../../shell/theme/shellTheme';

const densityValue = {
    compact: true,
    phone: false,
    sectionGap: 1.5,
    panelPadding: 1.5,
};

const StrategyDeskDrawer = ({ open, instance, onClose }) => {
    const view = useStrategyInstanceView(
        instance?.strategyId,
        instance?.clientId,
        open && Boolean(instance),
    );

    const actionBadge = useMemo(() => {
        if (!view) {
            return null;
        }
        if (view.meta?.family === 'vyasa') {
            return (
                <StatusBadge
                    label={view.connection === 'connected' ? 'Live' : 'Offline'}
                    tone={view.connectionTone}
                />
            );
        }
        return (
            <StatusBadge
                status={view.connection || 'waiting'}
                label={`Feed ${view.connection || 'waiting'}`}
                tone={view.connectionTone}
            />
        );
    }, [view]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 520, md: 640 },
                    bgcolor: shellTokens.surface,
                    borderLeft: `1px solid ${shellTokens.border}`,
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${shellTokens.border}`,
                        flexShrink: 0,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: shellTokens.muted,
                                }}
                            >
                                Strategy desk
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1rem', mt: 0.25 }}>
                                {instance?.strategyLabel || instance?.strategyId || 'Instance'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: shellTokens.muted, mt: 0.25 }}>
                                {clientColumnKey(instance || {})}
                                {instance?.mode ? ` · ${instance.mode}` : ''}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                {instance?.status ? (
                                    <Chip
                                        label={instance.status}
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                                    />
                                ) : null}
                                {instance?.isStale ? (
                                    <Chip
                                        label="stale"
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            bgcolor: 'rgba(217, 119, 6, 0.12)',
                                            color: shellTokens.warning,
                                        }}
                                    />
                                ) : null}
                                {instance?.isHalted ? (
                                    <Chip
                                        label="halted"
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            bgcolor: 'rgba(220, 38, 38, 0.1)',
                                            color: shellTokens.error,
                                        }}
                                    />
                                ) : null}
                            </Stack>
                        </Box>
                        <IconButton onClick={onClose} aria-label="Close strategy desk drawer" size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                    {!instance ? (
                        <Typography sx={{ color: shellTokens.muted, fontSize: '0.84rem' }}>
                            Select a fleet instance to inspect snapshot and events.
                        </Typography>
                    ) : !view ? (
                        <Typography sx={{ color: shellTokens.muted, fontSize: '0.84rem' }}>
                            Loading instance telemetry…
                        </Typography>
                    ) : (
                        <DensityContext.Provider value={densityValue}>
                            <Stack spacing={1.75}>
                                <StrategySnapshotPanel
                                    strategyFamily={view.meta.family}
                                    action={actionBadge}
                                    headlineMessage={view.snapshot.headlineMessage}
                                    stateCommentary={view.snapshot.stateCommentary}
                                    metrics={view.snapshot.metrics}
                                    modelScores={view.snapshot.modelScores}
                                    modelScoresAsOf={view.snapshot.modelScoresAsOf}
                                    minHeight={undefined}
                                />
                                <StrategyEventFeedPanel
                                    strategyLabel={view.strategyLabel}
                                    eventFeedRows={view.eventFeedRows}
                                    feedHeight={360}
                                />
                            </Stack>
                        </DensityContext.Provider>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default StrategyDeskDrawer;
